import classNames from 'classnames'
import React from 'react'

import {
  useIsJobDeleted,
  useNonDeletedPipelineJobIdsOrDraft,
  usePipelineDraftGraph,
} from '../../../../../../EditPipelinePage/EditPipelinePage.hooks'
import {pipelineDraft} from '../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import {usePipelineId} from '../../../../../../hooks/pipeline'
import SidebarFooter from '../../SidebarFooter/SidebarFooter'

import styles from './JobSettingsFooter.css'

import IconButton from 'src/components/common/IconButton/IconButton'
import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type Props = {
  jobId: string
}

function JobSettingsFooter({jobId}: Props) {
  const dispatch = useAppDispatch()

  const pipelineId = usePipelineId()
  const jobIds = useNonDeletedPipelineJobIdsOrDraft()
  const deleted = useAppSelector(state => state.pipelines.pipelineDraft[pipelineId]?.deleted?.jobs)
  const graph = usePipelineDraftGraph()

  const isSingleJob = jobIds.length === 1
  const hasNonDeletedDependants =
    graph[jobId] != null &&
    Array.from(graph[jobId].successors).some(successorId => !deleted?.includes(successorId))
  const isDeleted = useIsJobDeleted(jobId)
  const hasDeletedDependencies =
    graph[jobId] != null &&
    Array.from(graph[jobId].dependencies).some(dependencyId => deleted?.includes(dependencyId))

  const handlerJobDeleteClick = () =>
    dispatch(pipelineDraft.actions.deleteJob({id: pipelineId, jobId}))
  const handlerJobRestoreClick = () =>
    dispatch(pipelineDraft.actions.restoreJob({id: pipelineId, jobId}))

  const icon = isDeleted ? 'update' : 'trash'
  const clickHandler = isDeleted ? handlerJobRestoreClick : handlerJobDeleteClick
  const title = isDeleted ? 'Restore job' : 'Delete job'

  const isDisabled = isDeleted ? hasDeletedDependencies : isSingleJob || hasNonDeletedDependants

  const tooltipTitle = isDeleted
    ? hasDeletedDependencies
      ? 'This job depends on some deleted ones'
      : undefined
    : isSingleJob
    ? "Can't delete the only job"
    : hasNonDeletedDependants
    ? 'Some non-deleted jobs depend on this one'
    : undefined

  return (
    <SidebarFooter>
      {isDeleted && <span>{'Job deleted'}</span>}
      <span title={tooltipTitle}>
        <IconButton
          className={classNames({
            [styles.restoreJob]: isDeleted,
          })}
          iconClassName={classNames({
            [styles.restoreJobIcon]: isDeleted,
            [styles.disabled]: isDisabled,
          })}
          title={title}
          icon={icon}
          onClick={clickHandler}
          danger={!isDeleted}
          disabled={isDisabled}
        >
          {title}
        </IconButton>
      </span>
    </SidebarFooter>
  )
}

export default React.memo(JobSettingsFooter)
